import { supabase } from "@/integrations/supabase/client";
import type { ServiceType } from "@/lib/services";

export type VerificationReviewStatus = "pending" | "approved" | "rejected" | "changes_requested";

export const VERIFICATION_QUEUES: {
  slug: string;
  status: VerificationReviewStatus;
  label: string;
}[] = [
  { slug: "pending", status: "pending", label: "Pending Verification" },
  { slug: "approved", status: "approved", label: "Approved" },
  { slug: "rejected", status: "rejected", label: "Rejected" },
  { slug: "changes", status: "changes_requested", label: "Changes Requested" },
];

export const VERIFICATION_SERVICES: { value: ServiceType; label: string }[] = [
  { value: "sale", label: "Sale" },
  { value: "rent", label: "Rent" },
  { value: "lease", label: "Lease" },
  { value: "parking", label: "Parking" },
];

/** Review sections the admin ticks off before deciding. */
export const REVIEW_SECTIONS = [
  { key: "identity", label: "Identity" },
  { key: "selfie", label: "Selfie" },
  { key: "documents", label: "Documents" },
  { key: "agreement", label: "Agreement" },
  { key: "service", label: "Service" },
] as const;

export type ReviewSectionKey = (typeof REVIEW_SECTIONS)[number]["key"];

export const VERIFICATION_STATUS_META: Record<
  VerificationReviewStatus,
  { label: string; cls: string }
> = {
  pending: { label: "Pending", cls: "bg-amber-500/15 text-amber-700" },
  approved: { label: "Approved", cls: "bg-emerald-500/15 text-emerald-700" },
  rejected: { label: "Rejected", cls: "bg-destructive/10 text-destructive" },
  changes_requested: { label: "Changes Requested", cls: "bg-brand-soft text-brown-dark" },
};

export interface VerificationRow {
  id: string;
  user_id: string;
  service_type: ServiceType | null;
  verification_kind: string;
  listing_kind: string | null;
  listing_id: string | null;
  status: string;
  submitted_data: Record<string, unknown> | null;
  document_paths: string[] | null;
  reviewer_id: string | null;
  reviewer_notes: string | null;
  reviewed_at: string | null;
  created_at: string;
  partner?: {
    id: string;
    full_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
}

export interface VerificationHistoryRow {
  id: string;
  verification_id: string;
  partner_id: string;
  reviewer_id: string | null;
  service_type: ServiceType | null;
  decision: string;
  reason: string | null;
  notes: string | null;
  reviewed_sections: string[] | null;
  created_at: string;
}

export function kindLabel(kind: string) {
  return kind
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function formatDateTime(v?: string | null) {
  if (!v) return "—";
  return new Date(v).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export async function fetchVerifications(
  status: VerificationReviewStatus,
  service?: ServiceType | "all",
): Promise<VerificationRow[]> {
  let q = supabase
    .from("verification_requests")
    .select("*")
    .eq("status", status)
    .order("created_at", { ascending: false });
  if (service && service !== "all") q = q.eq("service_type", service);
  const { data, error } = await q;
  if (error) throw error;
  const rows = (data as unknown as VerificationRow[]) ?? [];

  const ids = Array.from(new Set(rows.map((r) => r.user_id)));
  if (ids.length === 0) return rows;
  const { data: profiles } = await supabase
    .from("profiles")
    .select("id, full_name, email, phone")
    .in("id", ids);
  const map = new Map((profiles ?? []).map((p) => [p.id, p]));
  return rows.map((r) => ({
    ...r,
    partner: (map.get(r.user_id) as VerificationRow["partner"]) ?? null,
  }));
}

export async function fetchVerificationHistory(
  verificationId: string,
): Promise<VerificationHistoryRow[]> {
  const { data, error } = await supabase
    .from("verification_reviews" as never)
    .select("*")
    .eq("verification_id", verificationId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as unknown as VerificationHistoryRow[]) ?? [];
}

/** Signed URLs for private verification documents (identity, selfie, docs). */
export async function signDocuments(
  paths: string[],
): Promise<{ path: string; url: string | null }[]> {
  const out: { path: string; url: string | null }[] = [];
  for (const path of paths) {
    const bucket =
      path.startsWith("verification/") || path.startsWith("agreements/")
        ? "landoo-private"
        : "landoo-media";
    const { data } = await supabase.storage.from(bucket).createSignedUrl(path, 60 * 30);
    out.push({ path, url: data?.signedUrl ?? null });
  }
  return out;
}

/**
 * Apply an admin decision. The verification row is updated (partner is notified
 * automatically by the database) and an immutable history entry is appended.
 */
export async function decideVerification(input: {
  verification: VerificationRow;
  reviewerId: string;
  decision: Exclude<VerificationReviewStatus, "pending">;
  reason?: string;
  notes?: string;
  sections: ReviewSectionKey[];
}): Promise<void> {
  const note = [input.reason, input.notes].filter(Boolean).join(" — ") || null;

  const { error } = await supabase
    .from("verification_requests")
    .update({
      status: input.decision,
      reviewer_id: input.reviewerId,
      reviewer_notes: note,
      reviewed_at: new Date().toISOString(),
    })
    .eq("id", input.verification.id);
  if (error) throw error;

  const { error: hErr } = await supabase.from("verification_reviews" as never).insert({
    verification_id: input.verification.id,
    partner_id: input.verification.user_id,
    reviewer_id: input.reviewerId,
    service_type: input.verification.service_type,
    decision: input.decision,
    reason: input.reason ?? null,
    notes: input.notes ?? null,
    reviewed_sections: input.sections,
  } as never);
  if (hErr) console.warn("[verification history] failed:", hErr.message);

  const { recordAudit } = await import("@/lib/backend");
  await recordAudit({
    action:
      input.decision === "approved"
        ? "admin_approval"
        : input.decision === "rejected"
          ? "admin_rejection"
          : "admin_action",
    entityType: "verification",
    entityId: input.verification.id,
    serviceType: input.verification.service_type,
    metadata: {
      decision: input.decision,
      partnerId: input.verification.user_id,
      reason: input.reason ?? null,
    },
  });
}

export const REJECT_REASONS = [
  "Identity document unclear or unreadable",
  "Selfie does not match identity document",
  "Documents expired or invalid",
  "Agreement not signed correctly",
  "Service details incomplete or inaccurate",
  "Suspected fraudulent submission",
];

export const CHANGE_REASONS = [
  "Upload a clearer identity document",
  "Retake the selfie in better lighting",
  "Add the missing supporting document",
  "Re-sign the agreement with correct details",
  "Update the service/business details",
];
